<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content">
      <div class="modal-card">

        <header class="modal-card-head">
          <p class="modal-card-title">Manage Cut Sheet Steps</p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <div class="modal-card-body">
          <table class="table is-fullwidth is-hoverable">
            <thead>
              <tr>
                <th 
                  v-for="(header, headerIndex) in headers" 
                  :key="headerIndex">
                  {{ header.label }}
                </th>
              </tr>
            </thead>

            <tfoot>
              <tr>
                <td :colspan="headers.length">
                  <div class="centered">
                    <button
                      v-if="!addingStep"
                      @click="addingStep = true" 
                      class="button is-primary is-small">
                      Add a new step
                    </button>
                  </div>
                </td>
              </tr>
            </tfoot>

            <tbody>
              <tr 
                v-for="step in cutSheetSteps" 
                :key="step.id">

                <template v-if="editingStepId != step.id">
                  <td>{{ step.name }}</td>
                  <td>{{ step.desc }}</td>
                  <td>{{ step.order_dependency }}</td>
                  <td>
                    <v-checkbox 
                      disabled 
                      v-model="step.required_by_default"/>
                  </td>
                </template>

                <template v-else>
                  <td>
                    <v-text-field 
                      required
                      placeholder="Enter Step Name"
                      v-model="step.name">
                    </v-text-field>
                  </td>
                  <td>
                    <v-text-field 
                      placeholder="Enter Step Description"
                      v-model="step.desc">
                    </v-text-field>
                  </td>
                  <td>
                    <v-text-field 
                      placeholder="Enter Step Description"
                      v-model="step.order_dependency">
                    </v-text-field>
                  </td>
                  <td>
                    <v-checkbox 
                      v-model="step.required_by_default"/>
                  </td>
                </template>
                <td>
                  <div class="buttons buttons-right">
                    <template v-if="editingStepId != step.id">
                      <button
                        @click="editingStepId = step.id" 
                        class="button is-small">
                        <span class="icon">
                          <i class="fas fa-edit has-text-grey"></i>
                        </span>
                      </button>
                      <button
                        @click="deleteCutSheetStep(step.id)" 
                        class="button is-small">
                        <span class="icon">
                          <i class="fas fa-trash has-text-grey"></i>
                        </span>
                      </button>
                    </template>
                    <template v-else>
                      <button
                        @click="updateCutSheetStep(step)" 
                        class="button is-primary is-small">
                        <span class="icon">
                          <i class="fas fa-save"></i>
                        </span>
                      </button>
                      <button
                        @click="editingStepId = null" 
                        class="button is-small">
                        <span class="icon">
                          <i class="fas fa-times has-text-grey"></i>
                        </span>
                      </button>
                    </template>
                  </div>
                </td>
              </tr>
              <tr v-if="addingStep">
                <td>
                  <v-text-field 
                    required
                    placeholder="Enter Step Name"
                    v-model="cutSheetStep.name">
                  </v-text-field>
                </td>
                <td>
                  <v-text-field 
                    placeholder="Enter Step Description"
                    v-model="cutSheetStep.desc">
                  </v-text-field>
                </td>
                <td>
                  <v-checkbox
                    v-model="cutSheetStep.required_by_default">
                  </v-checkbox>
                </td>
                <td>
                  <div class="buttons buttons-right">
                    <button
                      @click="addCutSheetStep" 
                      class="button is-primary is-small">
                      <span class="icon">
                        <i class="fas fa-save"></i>
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- <footer class="modal-card-foot">
          <button 
            :disabled="!valid"
            class="button is-primary" 
            @click.stop="addCutSheetStep()">
            Save
          </button>
          <button 
            class="button" 
            @click.stop="close(true)">
            Cancel
          </button>
        </footer> -->

      </div>
    </div>
  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VCheckbox from '@/components/base/VCheckbox'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    }
  },
  components: { VCheckbox, VTextField },
  data() {
    return {
      headers: [
        { label: 'Name' },
        { label: 'Description' },
        { label: 'Order' },
        { label: 'Required By Default' },
        { label: '' } 
      ],
      cutSheetStep: {
        order_dependency: null,
        name: '',
        desc: '',
        required_by_default: false
      },
      valid: false,
      addingStep: false,
      editingStepId: null
    }
  },
  computed: {
    ...mapState("cutSheets", ["cutSheetSteps"])
  },
  watch: {
    value(v) {
      if (!v) {
        this.addingStep = false;
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("cutSheets", [
      "getSteps","addStep", "deleteStep", "updateStep"
    ]),
    ...mapMutations("cutSheets", ["sortSteps"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.cutSheetSteps.length == 0) {
        this.getSteps();
      }
    },
    addCutSheetStep() {
      this.addStep(this.cutSheetStep).then(() => {
        this.setNotification({
          type: 'success',
          status: true,
          message: 'Cut sheet step successfully saved'
        });
        this.addingStep = false;
        this.sortSteps();
        this.clearData();
      });
    },
    updateCutSheetStep(step) {
      this.updateStep({ stepId: step.id, data: step }).then(() => {
        this.editingStepId = null;
        this.sortSteps();
      })
    },
    deleteCutSheetStep(stepId) {
      this.deleteStep(stepId);
      this.sortSteps();
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style scoped>
.buttons-right {
  justify-content: flex-end;
  flex-wrap: nowrap;
  align-items: center;
}

.table td {
  vertical-align: middle;
}

.table tfoot td .centered {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

.modal-card {
  border-radius: 6px;
}
</style>
